import {createSelector} from '@reduxjs/toolkit';
import {BILLING_ANNUALLY} from './services/subscription/billingCycle';
import {
	SUB_NONE,
	SUB_SCHEDULED_TO_CANCEL, SUB_SPECIAL,
	SUB_TRIAL_EXPIRED,
	SUB_TRIALING
} from './services/subscription/subscriptionStatus';
import {selectProfileById} from './slices/profileSlice';
import {profileDisplayName} from './util/profileDisplayName';
import {selectAllArtworkTypes, selectAllMaterials, selectAllSerie, selectAllTags} from './slices/artworkSlice';

export const selectArtworkQuery = state => state.artwork.query;
export const selectArtworkCounters = state => state.artwork.counters;
export const selectTagSuggestions = state => state.artwork.tagSuggestions;

export const selectAccountsQuery = state => state.account.query;
export const selectLogsQuery = state => state.activity.query;
export const selectActivities = state => state.activity.activities;

// status
export const selectLanguage = state => state.status.lang;
export const selectTenant = state => state.status.tenant;
export const selectAvailableContentLanguages = state => state.status.availableContentLanguages;
export const selectCurrentContentLanguage = state => state.status.currentContentLanguage;
export const selectUserRole = state => state.account.data.account?.role;

export const selectShowArtworksFilter = state => state.status.showArtworksFilter;

//profile
export const selectCurrentProfileId = state => state.profile.currentProfileId;
export const selectCurrentProfile = state => selectProfileById(state, state?.profile?.currentProfileId);



// permissions
export const selectGlobalPermissions = state => state.account.globalPermissions;
export const selectProfilePermissions = state => state.account.profilePermissions;

export const selectRole = state => state.account.data.account?.role;
export const selectCurrentAccountId = state => state.account.data.account?.id;
export const selectCurrentAccount = state => state.account.data.account;
export const selectLoggedIn = state => state.account.data.loggedIn;

export const selectIsOwnerOfCurrentProfile = createSelector(
	[selectCurrentProfile, selectCurrentAccountId],
	(profile, accountId) => (profile?.ownerId === accountId)
);
export const selectAccountInitials = createSelector([selectCurrentAccount], (account) =>
	account.name
		.match(/(^\S\S?|\s\S)?/g)
		.map((v) => v.trim())
		.join('')
		.match(/(^\S|\S$)?/g)
		.join('')
		.toLocaleUpperCase()
);

export const selectProfileDisplayName = createSelector(
	[selectCurrentProfile],
	(profile) => (profile ? profileDisplayName(profile) : '')
);
export const selectProfileSeriesLabel = createSelector(
	[selectCurrentProfile, selectLanguage],
	(profile, language) => {
		let customSeriesLabels = profile?.customLabels?.series;

		// default, when not from backend
		if (!customSeriesLabels) {
			customSeriesLabels = [
				{lang: 'de', value: 'Serien'},
				{lang: 'en', value: 'Series'},
			];
		}

		// find appropriate for language
		let seriesLabel = customSeriesLabels.find((label) => label.lang === language)?.value;

		if (!seriesLabel || seriesLabel === '') {
			// pick from first available language
			seriesLabel = customSeriesLabels[0]?.value ?? 'Serie';
		}
		if (!seriesLabel || seriesLabel === '') {
			// choose default label
			seriesLabel = language === 'de' ? 'Serien' : 'Series';
		}

		return seriesLabel;
	}
);

export const selectProfileDefaultLanguage = createSelector(
	[selectCurrentProfile],
	(profile) => (profile ? profile.defaultLang : 'en')
);

export const selectFiltersWithCounters = createSelector(
	[selectArtworkCounters, selectAllArtworkTypes, selectAllMaterials, selectAllSerie, selectAllTags],
	(counters, artworkTypes, materials, series, tags) => {

		// static options
		const statusOptions = [
			{
				id: 'draft',
				name: [
					{lang: 'en', value: 'Draft'},
					{lang: 'de', value: 'Entwurf'}
				]
			},
			{
				id: 'published',
				name: [
					{lang: 'en', value: 'Dataset OK'},
					{lang: 'de', value: 'Datensatz OK'}
				]
			}
		];
		const visibilityOptions = [
			{
				id: 'draft',
				name: [
					{lang: 'en', value: 'Draft'},
					{lang: 'de', value: 'In Bearbeitung'}
				]
			},
			{
				id: 'private',
				name: [
					{lang: 'en', value: 'Private'},
					{lang: 'de', value: 'Privat'}
				]
			},
			{
				id: 'public',
				name: [
					{lang: 'en', value: 'Public'},
					{lang: 'de', value: 'Öffentlich'}
				]
			}
		];
		const isCoreOptions = [
			{
				id: '1',
				name: [
					{lang: 'en', value: 'In Core Collection'},
					{lang: 'de', value: 'Im Kernkonvolut'},
				],
			},
			{
				id: '0',
				name: [
					{lang: 'en', value: 'Not in Core Collection'},
					{lang: 'de', value: 'Nicht im Kernkonvolut'},
				],
			},
		];

		// merge status and visibility in one filter
		// apply counters from both
		let visibilityOptionsWithCounters = mapCountersToOptions(visibilityOptions, counters?.visibility);
		visibilityOptionsWithCounters = mapCountersToOptions(visibilityOptionsWithCounters, counters?.status);

		return {
			status: mapCountersToOptions(statusOptions, counters?.status),
			visibility: visibilityOptions,
			isCore: mapCountersToOptions(isCoreOptions, counters?.isCore),
			types: mapCountersToOptions(artworkTypes, counters?.typeId),
			materials: mapCountersToOptions(materials, counters?.materialId),
			tags: mapCountersToOptions(tags, counters?.tagIds),
			series: mapCountersToSeriesOptions(series, counters?.seriesId),
			counters: counters,
		};
	}
);

// plans / current subscription
export const selectCurrentSubscription = (state) => state.account?.data.account?.subscription;
export const selectSubscribedPrice = createSelector(
	[selectCurrentSubscription],
	sub => (sub? sub.price : null)
);
export const selectSubscribedPlan = createSelector(
	[selectCurrentSubscription],
	sub => (sub? sub.plan : null)
);
export const selectSubscribedPlanName = createSelector(
	[selectSubscribedPlan, selectLanguage],
	(plan, lang) => (plan ? plan.name.find((n) => n.lang === lang).value : '')
);
export const selectPrices = state => state.plan.plans.reduce((acc, plan) => acc.concat(plan.prices), []);
export const selectPlans = state => {
	const currentPlan = selectSubscribedPlan(state);
	return (state.plan.plans.map(plan => {
		const isCurrent = currentPlan && currentPlan.id === plan.id;
		const unpriced = plan.free || plan.priceOnRequest;
		const isUpgrade = currentPlan && !currentPlan.free && !unpriced && plan.upgradeSortOrder > currentPlan.upgradeSortOrder;
		const isDowngrade = currentPlan && !unpriced && plan.upgradeSortOrder < currentPlan.upgradeSortOrder;
		const canChoose = !isCurrent && (
			isUpgrade ||
			(state.status.allowDowngrades && isDowngrade) ||
			(!currentPlan && !unpriced) ||
			(currentPlan && !isCurrent && currentPlan.free)
		);
		plan = {
			...plan,
			isCurrent: isCurrent,
			isUpgrade: isUpgrade,
			isDowngrade: isDowngrade,
			canChoose: canChoose,
		};
		return plan;
	}) || []);
};
export const selectIsTrialing = createSelector(
	[selectCurrentSubscription],
	sub => sub && sub.status === SUB_TRIALING
);
export const selectSubscriptionStatus = createSelector(
	[selectCurrentAccount, selectCurrentSubscription],
	(acc, sub) => {
		if (!sub) {
			if (acc && (acc.role === 'ADMIN' || acc.invitedBy > 0)) {
				return SUB_SPECIAL;
			}
			return SUB_NONE;
		}
		if (sub.scheduledToCancelAt) {
			return SUB_SCHEDULED_TO_CANCEL; // necessary?
		}
		return sub.status;
	}
);
export const selectTrialEndsAt = createSelector(
	[selectCurrentSubscription],
	sub => (sub && sub.trialEndsAt? new Date(sub.trialEndsAt) : null)
);
const daysUntil = (targetDate) => {
	if (!targetDate) return '?';
	const diff = targetDate - new Date(); // ms
	let days = Math.round(diff / (1000 * 60 * 60 * 24)); // do we need to consider time zones? use data-fns
	days = Math.max(0, days);
	return days;
};
export const selectTrialDaysLeft = createSelector([selectTrialEndsAt], daysUntil);
export const selectScheduledToCancelAt = createSelector(
	[selectCurrentSubscription],
	sub => (sub && sub.scheduledToCancelAt? new Date(sub.scheduledToCancelAt) : null)
);
export const selectCancelDaysLeft = createSelector([selectScheduledToCancelAt], daysUntil);
export const selectCurrentBillingCycle = (state) => {
	const price = selectSubscribedPrice(state);
	return price?.billingCycle || BILLING_ANNUALLY;
};
export const selectShowSubscriptionBanner = createSelector([selectSubscriptionStatus], (status) => (
	[SUB_TRIALING, SUB_TRIAL_EXPIRED].includes(status))
);

// subscriptions (admin)

function mapCountersToOptions(options, counters) {
	return options.map(option => {
		if (counters === undefined || counters.length < 2) {
			return option;
		}
		const counter = counters.find(c => c[0] + '' === option.id + '');
		// fix counters in Backend
		if (counter) {
			return {
				...option,
				counter: counter[1]
			};
		} else {
			return option;
		}
	});
}

function mapCountersToSeriesOptions(series, counters) {
	return series.map(serie => {
		if (counters === undefined || counters.length < 2) {
			return {
				id: serie.id,
				name: serie.title,
				counter: null
			};
		}
		const counter = counters.find(c => c[0] + '' === serie.id + '');
		return {
			id: serie.id,
			name: serie.title,
			counter: counter[1]
		};
	});
}
